/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabriele
 * File: RecipeSwitcherFeature.ts
 * Project: mle-client-n
 * Committed last: 2022/6/13 @ 1316
 * ---
 * Description:
 */

import {EditorFeature} from '../common/EditorFeature';
import {Editor} from '../../observables/Editor';
import {Metadata} from '@revodigital/mpes';
import {action, computed, makeObservable, observable} from 'mobx';
import {MetaGroup, MetaKey} from '@revodigital/mpes/lib';
import {mlAuthSecured} from './auth/MyLabelAuthFeature';
import {MyLabelCompanyMeta, MyLabelRecipeMeta, MyLabelRevision} from '../../../entities';
import {mleDomainGqlClient} from '../../../api/domain';
import {GET_AVAILABLE_RECIPES, GET_COMPANIES_META, GET_REVISIONS_INFO} from '../../../api/domain/queries.gql';

export const RECIPE_SWITCHER_FNAME = 'recipe-switcher';

export const ACTIVE_STATE_GROUP = 'active_state';
export const ACTIVE_RECIPE = 'active_revision';
export const ACTIVE_COMPANY = 'active_company';

export enum SuggestionPolicy {
  FIRST_AVAILABLE,
  FIRST_WITH_DATA,
  LAST
}

export class RevisionSwitcherFeature extends EditorFeature<void> {
  constructor(editor: Editor) {
    super(RECIPE_SWITCHER_FNAME, editor);
    this._activeCompany = 0;
    this._activeRevision = 0;

    makeObservable(this);
  }

  @observable
  private _activeRevision: number;

  @computed
  public get activeRevision() {
    return this._activeRevision;
  }

  @observable
  private _activeCompany: number;

  @computed
  public get activeCompany() {
    return this._activeCompany;
  }

  @computed
  public get hasActiveRevision() {
    return this._activeRevision !== null && this._activeRevision > 0;
  }

  @computed
  public get hasActiveCompany() {
    return this._activeCompany !== null && this._activeCompany > 0;
  }

  init(): boolean {
    const metadata = new Metadata(this.sourcePointer.projectData.projectOptions.metadata);
    this._activeRevision = metadata.getKeyValueOfGroup<number>(ACTIVE_STATE_GROUP,
        ACTIVE_RECIPE) as number;
    this._activeCompany = metadata.getKeyValueOfGroup(ACTIVE_STATE_GROUP,
        ACTIVE_COMPANY) as number;

    console.info("Active revision: ", this._activeRevision);
    console.info("Active company: ", this._activeCompany);

    if (!this._activeRevision || !this._activeCompany)
      console.warn("No active revision set: ", this._activeCompany, this._activeRevision);

    return true;
  }

  writeToMetadata(metadata: Metadata) {
    const g = new MetaGroup({
      title: ACTIVE_STATE_GROUP,
      keys: [MetaKey.init(ACTIVE_RECIPE, this._activeRevision), MetaKey.init(
          ACTIVE_COMPANY,
          this._activeCompany)],
      payload: {}
    });

    metadata.addGroup(g);
  }

  @action
  public setActiveRevision(value: number) {
    this._activeRevision = value;
  }

  @action
  public setActiveCompany(value: number) {
    this._activeCompany = value;
  }

  /**
   * Restituisce l'organizzazione che è consigliata per questa etichetta. Possono essere applicate diverse policy,
   * tramite il quale si può personalizzare l'algoritmo.
   * @param policy
   * @returns undefined se non è possibile scegliere una organizzazione oppure la company suggerita
   */
  public async getSuggestedCompany(policy: SuggestionPolicy): Promise<MyLabelCompanyMeta | undefined> {
    // Check connection and credentials
    const companiesMeta = await mlAuthSecured<MyLabelCompanyMeta[]>(this.sourcePointer,
        async () => {
          return (await mleDomainGqlClient.query({
            query: GET_COMPANIES_META,
          })).data.getCompaniesMeta;
        });

    if (!companiesMeta || companiesMeta.length === 0) return undefined;

    switch (policy) {
      case SuggestionPolicy.FIRST_AVAILABLE:
        return companiesMeta[1];
      case SuggestionPolicy.FIRST_WITH_DATA:
        // Iterate all companies and get the first with some data inside
        for (const company of companiesMeta) {
          const recipesMeta = await mlAuthSecured<MyLabelRecipeMeta[]>(this.sourcePointer,
              async () => {
                return (await mleDomainGqlClient.query({
                  query: GET_AVAILABLE_RECIPES,
                  variables: {
                    companyId: company.id
                  }
                })).data.getMyLabelRecipesMeta as MyLabelRecipeMeta[];
              });

          if (recipesMeta && recipesMeta.length > 0) return company;
        }

        return undefined;
      case SuggestionPolicy.LAST:
        return companiesMeta[companiesMeta.length - 1];
    }
  }

  /**
   * Restituisce la revisione che è consigliata per questa etichetta (la prima)
   * @param companyId Organizzazione a cui deve appartenere
   * @returns La ricetta suggerita oppure undefined
   */
  public async getSuggestedRecipe(companyId: number): Promise<MyLabelRevision | undefined> {
    const recipesMeta = await mlAuthSecured<MyLabelRevision[]>(this.sourcePointer,
        async () => {
          return (await mleDomainGqlClient.query({
            query: GET_REVISIONS_INFO,
            variables: {
              companyId: companyId
            }
          })).data.getLabelRevisions as MyLabelRevision[];
        });

    if (recipesMeta && recipesMeta.length > 0)
      return recipesMeta[recipesMeta.length - 1];

    return undefined;
  }

  /**
   * Prova a selezionare automaticamente una ricetta e un'organizzazione attiva.
   * Restituisce i dati della selezione oppure undefined se non è possibile
   * selezionarla automaticamente
   */
  @action
  public async tryAutoSelect(): Promise<{ company: MyLabelCompanyMeta, recipe: MyLabelRevision } | undefined> {
    const suggestedCompany = await this.getSuggestedCompany(
        SuggestionPolicy.FIRST_AVAILABLE);

    if (suggestedCompany) {
      const suggestedRecipe = await this.getSuggestedRecipe(
          suggestedCompany.id);

      if (suggestedRecipe) {
        this.setActiveCompany(suggestedCompany.id);
        this.setActiveRevision(suggestedRecipe.id);

        return {
          company: suggestedCompany,
          recipe: suggestedRecipe
        };
      }
    }

    return undefined;
  }

  destroy(): boolean {
    return false;
  }
}